import { siteStickerUrl, stickerUrl } from "@listou/shared/stickers";

import type { BlogPost } from "../../lib/blog";
import { CARDS, INLINE_CTA } from "../../lib/blog-markers";

export const listeDeNaissanceGratuiteMultiEnseignes: BlogPost = {
  h1: "Liste de naissance gratuite et multi-enseignes : comment ça marche, et où sont les frais",
  seoTitle: "Liste de naissance gratuite et multi-enseignes : les frais à vérifier · Listou",
  seoDescription:
    "Une liste de naissance gratuite, vraiment ? Ce que veut dire « multi-enseignes », les frais qui se cachent derrière les participations en argent et les options payantes, et les quatre questions à poser avant de choisir.",
  publishedOn: "2026-10-26",
  updatedOn: "2026-10-26",
  summary:
    "Une liste de naissance multi-enseignes réunit des envies de n'importe quelle boutique sur une seule page : les proches achètent où ils veulent et réservent le cadeau sur la liste. La création est presque toujours gratuite ; les frais, quand il y en a, se trouvent ailleurs : une commission sur les participations en argent, des options payantes, ou une limite de cadeaux. Avant de choisir, vérifiez ces trois points et ce que voient vos proches.",
  body: `
## Liste d'enseigne ou liste multi-enseignes

Il existe deux grandes familles de listes de naissance.

- **La liste d'une enseigne** : un magasin de puériculture ou un grand site héberge votre liste. Les proches achètent chez lui, et l'enseigne offre souvent un avantage en échange, comme un bon d'achat calculé sur le montant de la liste. Le revers : tous les cadeaux viennent du même catalogue.
- **La liste multi-enseignes** : un site indépendant de toute boutique. Vous y collez le lien d'une poussette chez un spécialiste, d'un body chez un créateur, d'un livre chez votre libraire. Les proches achètent où ils veulent, puis réservent le cadeau sur la liste pour que personne d'autre ne l'offre.

La liste multi-enseignes est devenue la plus courante : on ne trouve jamais tout ce qu'on veut dans un seul magasin, et les proches préfèrent souvent acheter là où ils ont leurs habitudes.

## « Gratuite » : ce qu'il faut vérifier

Presque toutes les listes se créent gratuitement. Les différences se cachent dans trois endroits.

### Les participations en argent

C'est le premier poste de frais. Quand un proche verse de l'argent pour la poussette ou pour l'épargne de bébé, certains services prélèvent une **commission sur chaque versement**, ou font payer la carte bancaire. Sur une liste où la famille participe beaucoup, la différence se chiffre vite en dizaines d'euros.

D'autres services ne prennent rien : ils proposent un virement direct, ou un lien vers une cagnotte que vous choisissez, avec ses propres conditions.

### Les options payantes

Une liste gratuite peut limiter le **nombre de cadeaux réservables**, les **thèmes**, ou certaines fonctions comme la surprise, puis proposer une formule payante pour les débloquer. Regardez ce que la version gratuite permet réellement avant de remplir quarante envies.

### Ce que voient vos proches

Un compte obligatoire pour réserver, de la publicité sur la page de la liste, des emails commerciaux après la naissance : ce sont aussi des coûts, payés par vos proches. Ouvrez votre liste comme eux, depuis un autre téléphone, avant de l'envoyer.

## Les quatre questions à poser avant de choisir
${CARDS}
Notre [comparatif des listes de naissance](/blog/meilleure-liste-de-naissance) répond à ces questions service par service, sources à l'appui.
${INLINE_CTA}
## Comment fonctionne Listou

Listou est notre service. Voici précisément ce qu'il fait, et ce qu'il ne fait pas :

- **la liste est gratuite**, comme la réservation, sans formule payante ;
- **multi-enseignes** : on colle le lien d'un produit de n'importe quelle boutique, et le nom, la photo et le prix sont récupérés ; une envie sans lien fonctionne aussi ;
- **sans compte pour les proches** : ils ouvrent le lien, réservent, c'est fait ;
- **la surprise par défaut** : vous ne savez pas qui a réservé quoi avant le lendemain de la date de l'événement ;
- **pas d'argent qui transite** : Listou ne gère pas de participations. Vous ajoutez un lien vers la cagnotte de votre choix, qui applique ses propres conditions ;
- **comment Listou se rémunère** : par une petite commission quand un cadeau est acheté via un lien vers une boutique partenaire, sans changer le prix payé.

Les gros cadeaux s'offrent à plusieurs sans argent à faire transiter : une poussette à 600 € notée en six parts de 100 € se réserve part par part, et celui qui réserve la dernière part passe la commande.

## Faire sa liste

Pour savoir quoi y mettre, notre article [que mettre sur une liste de naissance](/blog/que-mettre-liste-de-naissance) passe en revue chaque rayon, et notre guide [quand faire et partager sa liste](/blog/liste-de-naissance-quand-partager) donne le bon calendrier. Pour vous lancer, la [liste de naissance](/liste-de-naissance) se crée en deux minutes.
`,
  faq: [
    [
      "Une liste de naissance est-elle vraiment gratuite ?",
      "La création l'est presque toujours. Les frais, quand il y en a, viennent d'une commission sur les participations en argent, d'options payantes ou d'une limite de cadeaux dans la version gratuite. Sur Listou, créer une liste et réserver sont gratuits, sans formule payante.",
    ],
    [
      "Qu'est-ce qu'une liste de naissance multi-enseignes ?",
      "Une liste indépendante de toute boutique, où l'on réunit des envies de n'importe quel site ou magasin. Les proches achètent où ils veulent, puis réservent le cadeau sur la liste pour éviter les doublons.",
    ],
    [
      "Liste d'enseigne ou multi-enseignes, que choisir ?",
      "La liste d'une enseigne offre souvent un avantage, comme un bon d'achat, mais tous les cadeaux viennent du même catalogue. La liste multi-enseignes laisse choisir chaque cadeau là où il est le meilleur, et laisse les proches acheter où ils ont leurs habitudes.",
    ],
    [
      "Peut-on recevoir de l'argent sur une liste de naissance sans frais ?",
      "Certains services proposent un virement direct sans commission, d'autres prélèvent un pourcentage sur chaque versement. Listou ne fait pas transiter d'argent : vous ajoutez un lien vers la cagnotte de votre choix, qui applique ses propres conditions.",
    ],
    [
      "Comment Listou gagne-t-il de l'argent si la liste est gratuite ?",
      "Par une petite commission quand un cadeau est acheté via un lien vers une boutique partenaire. Le prix payé par vos proches ne change pas.",
    ],
  ],
  occasion: "birth",
  inlineCta: {
    title: "Une liste de naissance gratuite, de toutes les boutiques",
    text: "Collez vos liens, partagez la liste, gardez la surprise. Sans compte pour vos proches.",
    sticker: stickerUrl("bebe", "nounours"),
  },
  cards: [
    {
      title: "Qui paie les participations ?",
      text: "Commission sur chaque versement, frais de carte, ou rien du tout.",
      sticker: stickerUrl("rayons-souvenirs", "tirelire"),
    },
    {
      title: "Que permet la version gratuite ?",
      text: "Nombre de cadeaux, surprise, thèmes : ce qui reste sans formule payante.",
      sticker: stickerUrl("rayons-cadeaux", "etiquette"),
    },
    {
      title: "Faut-il un compte pour réserver ?",
      text: "Chaque étape en plus, c'est un grand-parent qui renonce.",
      sticker: siteStickerUrl("cadenas"),
    },
    {
      title: "Qui voit les réservations ?",
      text: "Les parents suivent tout, ou la surprise est gardée jusqu'à la naissance.",
      sticker: stickerUrl("bebe", "cigogne"),
    },
  ],
};
